import { useCallback } from 'react';
import { useAuth as useClerkAuth, useUser, useClerk } from '@clerk/clerk-react';
import { JWTContextType } from 'src/types/auth.types';
import { useProfile } from 'src/hooks/profile/useProfile';

// Clerk-backed shim for the original Cognito-driven useAuth hook.
// Replaces 2026-04-20. JWTContextType return shape preserved so
// existing consumers (nav-user, Profile, etc.) don't change.
//
// Mapping:
//   isAuthenticated  ← Clerk isSignedIn
//   isInitialized    ← Clerk isLoaded (and profile resolved if signed in)
//   profile          ← EIQ User row from useProfile() (Clerk only knows
//                      identity; the EIQ User row is the source of truth
//                      for the app-side UserProfile shape — populated
//                      by the Clerk webhook on user.created)
//   isOnboarded      ← profile present
//   login            ← no-op stub. Clerk's <SignIn> drives session
//                      creation; programmatic login isn't used.
//   logout           ← Clerk signOut()
export const useAuth = (): JWTContextType => {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { profile, loading: profileLoading } = useProfile();

  const login = useCallback(async (): Promise<void> => {
    // Clerk's <SignIn> drives session creation. Programmatic login is
    // not part of the new flow — no-op kept so JWTContextType holds.
    return;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await signOut();
  }, [signOut]);

  const initialized = !!isLoaded && (!isSignedIn || !profileLoading);

  return {
    isAuthenticated: !!isSignedIn,
    isInitialized: initialized,
    method: 'jwt',
    profile: profile ?? null,
    isOnboarded: !!profile,
    login,
    logout,
    // Pass through Clerk identity for consumers that need email / userId
    // without re-importing useUser. Untyped escape hatch.
    ...({
      clerkUserId: clerkUser?.id,
      clerkEmail: clerkUser?.primaryEmailAddress?.emailAddress,
    } as object),
  };
};
